import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Star, BookOpen, Plus, Check, X } from "lucide-react";

// Selector unificado de artículos para producción (read-model /produccion/selector).
// Fuentes separadas: "Habitual DNF" (materiales_tarifas) y "Catálogo/Tarifa" (catalogo_precios). Stock NO interviene.
const FUENTES = [
  { k: "habituales", l: "Habituales DNF" },
  { k: "catalogo", l: "Catálogo/Tarifa" },
  { k: "todos", l: "Todos" },
];

export function FuenteBadge({ fuente }) {
  const hab = fuente === "habitual";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[9px] uppercase px-1.5 py-0.5 rounded border ${hab ? "bg-amber-500/15 text-amber-200 border-amber-500/40" : "bg-sky-500/15 text-sky-200 border-sky-500/40"}`}
      data-testid={`fuente-badge-${fuente}`}>
      {hab ? <Star className="w-2.5 h-2.5" /> : <BookOpen className="w-2.5 h-2.5" />} {hab ? "Habitual DNF" : "Catálogo/Tarifa"}
    </span>
  );
}

export default function SelectorArticuloProduccion({ value, onSelect, onClear, onMaterialesChanged, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [fuente, setFuente] = useState("habituales");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/produccion/selector", { params: { q, fuente } })
        .then(({ data }) => alive && setItems(data.items || []))
        .catch(() => alive && setItems([]))
        .finally(() => alive && setLoading(false));
    }, 200);
    return () => { alive = false; clearTimeout(t); };
  }, [open, q, fuente]);

  const label = useMemo(() => value?.nombre ? `${value.nombre} (${value.unidad || "ud"})` : "", [value]);

  const anadirHabitual = async (it, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post("/produccion/selector/anadir-habitual", { catalogo_id: it.fuente_id });
      toast.success(data.ya_existia ? "Ya estaba en habituales" : "Añadido a habituales DNF (sin stock)");
      onMaterialesChanged && onMaterialesChanged();
      const m = data.material;
      onSelect({ ref: `habitual:${m.id}`, fuente: "habitual", fuente_id: m.id, material_id: m.id, nombre: m.nombre, categoria: m.categoria, unidad: m.unidad,
        precio_coste: m.precio_coste || 0, precio_venta: m.precio_venta || 0, merma_pct: m.merma_pct || 0, proveedor_id: m.proveedor_id || null });
      setOpen(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo añadir a habituales"); }
  };

  return (
    <div className="relative" data-testid="selector-articulo">
      <button type="button" disabled={disabled} onClick={() => setOpen((v) => !v)} data-testid="selector-articulo-trigger"
        className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm text-left hover:border-zinc-700 disabled:opacity-50">
        <span className={`truncate flex items-center gap-2 ${label ? "text-zinc-100" : "text-zinc-500"}`}>
          {value?.fuente && <FuenteBadge fuente={value.fuente} />}
          {label || "Buscar material o artículo…"}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {label && onClear && <X className="w-3.5 h-3.5 text-zinc-500 hover:text-zinc-200" data-testid="selector-articulo-clear" onClick={(e) => { e.stopPropagation(); onClear(); }} />}
          <Search className="w-3.5 h-3.5 text-zinc-500" />
        </span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-zinc-800 bg-zinc-950 shadow-xl" data-testid="selector-articulo-panel">
          <div className="p-2 border-b border-zinc-800 space-y-1.5">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar material o artículo…" data-testid="selector-articulo-input"
              className="w-full h-8 px-2 rounded bg-zinc-900 border border-zinc-800 text-sm text-zinc-100 outline-none focus:border-amber-500/50" />
            <div className="flex gap-1">
              {FUENTES.map((f) => (
                <button key={f.k} type="button" onClick={() => setFuente(f.k)} data-testid={`selector-fuente-${f.k}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full border ${fuente === f.k ? "bg-amber-500 text-zinc-900 border-amber-500" : "bg-transparent text-zinc-400 border-zinc-700 hover:text-zinc-200"}`}>
                  {f.l}
                </button>
              ))}
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto divide-y divide-zinc-900" data-testid="selector-articulo-lista">
            {loading && <li className="px-3 py-2 text-xs text-zinc-500">Buscando…</li>}
            {!loading && items.length === 0 && <li className="px-3 py-2 text-xs text-zinc-500">Sin resultados en {FUENTES.find((f) => f.k === fuente)?.l}.</li>}
            {items.map((it) => (
              <li key={it.ref} data-testid={`selector-item-${it.ref}`}>
                <button type="button" onClick={() => { onSelect(it); setOpen(false); }}
                  className="w-full text-left px-3 py-1.5 hover:bg-zinc-900 flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate flex items-center gap-2">
                      {value?.ref === it.ref && <Check className="w-3 h-3 text-emerald-400" />}{it.nombre}
                    </div>
                    <div className="text-[10px] text-zinc-500 flex items-center gap-1.5 flex-wrap">
                      <FuenteBadge fuente={it.fuente} />
                      <span>{it.unidad}</span>
                      {it.categoria && <span>· {it.categoria}</span>}
                      {it.precio_venta ? <span>· venta {Number(it.precio_venta).toFixed(2)} €/{it.unidad}</span> : null}
                      {it.precio_coste ? <span>· coste {Number(it.precio_coste).toFixed(2)} €</span> : null}
                    </div>
                  </div>
                  {it.fuente === "catalogo" && (
                    <span role="button" onClick={(e) => anadirHabitual(it, e)} title="Crear material habitual DNF desde este artículo (no crea stock)"
                      data-testid={`selector-anadir-habitual-${it.fuente_id}`}
                      className="shrink-0 text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10">
                      <Plus className="w-3 h-3" /> a habituales
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
